import { RefObject, useContext, useRef, useState } from "react";

import {
  CellClickedEventArgs,
  DataEditor,
  DataEditorProps,
  DataEditorRef,
  DrawCellCallback,
  GridKeyEventArgs,
  GridMouseEventArgs,
  GridSelection,
  HeaderClickedEventArgs,
  Item,
} from "@glideapps/glide-data-grid";

import { DatagridFitContentContext } from "./datagrid-fit-content-context";
import { DataGridData, useDataGridConfig } from "./use-data-grid-config";

import "@glideapps/glide-data-grid/dist/index.css";

export interface RowContextMenuPosition {
  rowIndex: number;
  pageX: number;
  pageY: number;
}

export interface CellClickedPosition {
  rowIndex: number;
  columnIndex: number;
  pageX: number;
  pageY: number;
}

interface DatagridProps {
  data: DataGridData[];
  columns: string[];
  columnIds?: string[];
  readOnlyColumns: string[];
  readOnlyRows: string[];
  handleCellSelection: (cell: { rowId: string; columnId: string; value: string | number }) => void;
  handleCellUpdate?: (cell: { rowId: string; columnId: string; value: string | number | null }) => void;
  onDelete?: (selection: GridSelection) => boolean | GridSelection;
  drawCell?: DrawCellCallback;
  allowNegativeValues?: boolean;
  onPaste?: (target: Item, values: readonly (readonly string[])[]) => boolean;
  handleSelectionChange?: (selection: GridSelection) => void;
  defaultSelection?: GridSelection;
  rows?: number;
  showUnitInHeader?: boolean;
  unit?: string | ((columnIndex: number, columnId: string) => string);
  props?: DataEditorProps;
  onItemHovered?: (args: GridMouseEventArgs) => void;
  columnTooltips?: (string | undefined)[];
  onRowAppended?: () => void;
  trailingRowHint?: string;
  onRowContextMenu?: (position: RowContextMenuPosition) => void;
  onHeaderContextMenu?: (position: HeaderContextMenuPosition) => void;
  onHeaderClicked?: (position: HeaderContextMenuPosition) => void;
  onCellClicked?: (position: CellClickedPosition) => void;
  placeholderColumnIds?: string[];
  gridRef?: RefObject<DataEditorRef>;
}

export interface HeaderContextMenuPosition {
  columnIndex: number;
  pageX: number;
  pageY: number;
}

export const Datagrid = ({
  data,
  columns,
  columnIds,
  readOnlyColumns,
  readOnlyRows,
  handleCellSelection,
  handleCellUpdate,
  onDelete,
  drawCell,
  allowNegativeValues,
  onPaste,
  handleSelectionChange,
  defaultSelection,
  rows,
  showUnitInHeader,
  unit,
  onItemHovered: externalOnItemHovered,
  columnTooltips,
  onRowAppended,
  trailingRowHint,
  onRowContextMenu,
  onHeaderContextMenu,
  onHeaderClicked,
  onCellClicked,
  placeholderColumnIds,
  gridRef,
  ...props
}: DatagridProps): JSX.Element => {
  const fitsContent = useContext(DatagridFitContentContext);
  const { defaultDataGridConfig } = useDataGridConfig({
    data,
    columns,
    columnIds,
    readOnlyColumns,
    readOnlyRows,
    handleCellSelection,
    handleCellUpdate,
    allowNegativeValues,
    handleSelectionChange,
    defaultSelection,
    rows,
    showUnitInHeader,
    unit,
    trailingRowHint,
    placeholderColumnIds,
    fitsContent,
    hasTrailingRow: onRowAppended !== undefined,
  });

  const wrapperRef = useRef<HTMLDivElement>(null);
  const internalGridRef = useRef<DataEditorRef>(null);
  const editorRef = gridRef ?? internalGridRef;
  const focusMarkerRef = useRef<HTMLDivElement>(null);

  const revealFocusedCell = (cell: Item | undefined) => {
    const cellBounds = cell && editorRef.current?.getBounds(cell[0], cell[1]);
    const wrapperRect = wrapperRef.current?.getBoundingClientRect();
    const marker = focusMarkerRef.current;
    if (!cellBounds || !wrapperRect || !marker) return;
    marker.style.top = `${cellBounds.y - wrapperRect.top}px`;
    marker.style.height = `${cellBounds.height}px`;
    marker.scrollIntoView({ block: "nearest" });
  };

  const handleFittedSelectionChange = (selection: GridSelection) => {
    defaultDataGridConfig.onGridSelectionChange(selection);
    revealFocusedCell(selection.current?.cell);
  };
  const [tooltip, setTooltip] = useState<{ col: number; text: string; x: number; y: number } | null>(null);

  const handleItemHovered = (args: GridMouseEventArgs) => {
    externalOnItemHovered?.(args);

    if (args.kind !== "header") {
      setTooltip((prev) => (prev ? null : prev));
      return;
    }

    const [col] = args.location;
    const tooltipText = columnTooltips?.[col];
    const headerText = columns[col];

    if (!tooltipText || tooltipText === headerText) {
      setTooltip((prev) => (prev ? null : prev));
      return;
    }

    setTooltip((prev) => {
      if (prev && prev.col === col) return prev;
      const wrapperRect = wrapperRef.current?.getBoundingClientRect();
      if (!wrapperRect) return prev;
      const localX = args.bounds.x - wrapperRect.left;
      const localY = args.bounds.y - wrapperRect.top;
      return {
        col,
        text: tooltipText,
        x: Math.max(8, localX + args.bounds.width / 2),
        y: localY + args.bounds.height + 4,
      };
    });
  };

  const handleCellContextMenu = (cell: Item, event: CellClickedEventArgs) => {
    if (!onRowContextMenu) return;
    const [col, row] = cell;
    if (col !== 0 || row < 0 || row >= data.length) return;
    event.preventDefault();
    onRowContextMenu({
      rowIndex: row,
      pageX: event.bounds.x + event.localEventX,
      pageY: event.bounds.y + event.localEventY,
    });
  };

  const handleHeaderContextMenu = (colIndex: number, event: HeaderClickedEventArgs) => {
    if (!onHeaderContextMenu || colIndex < 0) return;
    event.preventDefault();
    onHeaderContextMenu({
      columnIndex: colIndex,
      pageX: event.bounds.x + event.localEventX,
      pageY: event.bounds.y + event.localEventY,
    });
  };

  const handleHeaderClicked = (colIndex: number, event: HeaderClickedEventArgs) => {
    if (!onHeaderClicked || colIndex < 0) return;
    onHeaderClicked({
      columnIndex: colIndex,
      pageX: event.bounds.x + event.localEventX,
      pageY: event.bounds.y + event.localEventY,
    });
  };

  // Keyboard equivalent of the right-click affordances so the row/column action
  // menus are reachable without a mouse (WCAG): the context-menu key (or
  // Shift+F10) opens the menu for the focused cell, and Enter opens the row menu
  // from the read-only energy-source name column (column 0).
  const handleGridKeyDown = (event: GridKeyEventArgs) => {
    const { location, bounds } = event;
    if (!location || !bounds) return;
    const [col, row] = location;
    const isMenuKey = event.key === "ContextMenu" || (event.shiftKey && event.key === "F10");
    const pageX = bounds.x + bounds.width / 2;
    const pageY = bounds.y + bounds.height;

    if (onRowContextMenu && col === 0 && row >= 0 && row < data.length && (isMenuKey || event.key === "Enter")) {
      event.cancel();
      event.stopPropagation();
      onRowContextMenu({ rowIndex: row, pageX, pageY });
      return;
    }

    if (onHeaderContextMenu && col > 0 && isMenuKey) {
      event.cancel();
      event.stopPropagation();
      onHeaderContextMenu({ columnIndex: col, pageX, pageY });
    }
  };

  const handleCellClicked = (cell: Item, event: CellClickedEventArgs) => {
    if (!onCellClicked) return;
    const [col, row] = cell;
    if (col < 0 || row < 0 || row >= data.length) return;
    // Unlike the context-menu handlers (which report the pointer position),
    // cell clicks report the cell's bottom-left corner so popovers anchored to
    // the click open flush under the cell instead of at the cursor.
    onCellClicked({
      columnIndex: col,
      rowIndex: row,
      pageX: event.bounds.x,
      pageY: event.bounds.y + event.bounds.height,
    });
  };

  return (
    <div ref={wrapperRef} className="relative h-full w-full overflow-auto">
      <DataEditor
        {...defaultDataGridConfig}
        {...props}
        ref={editorRef}
        onGridSelectionChange={fitsContent ? handleFittedSelectionChange : defaultDataGridConfig.onGridSelectionChange}
        drawCell={drawCell}
        onPaste={onPaste}
        onDelete={onDelete}
        onItemHovered={handleItemHovered}
        onRowAppended={onRowAppended}
        onCellContextMenu={onRowContextMenu ? handleCellContextMenu : undefined}
        onHeaderContextMenu={onHeaderContextMenu ? handleHeaderContextMenu : undefined}
        onHeaderClicked={onHeaderClicked ? handleHeaderClicked : undefined}
        onCellClicked={onCellClicked ? handleCellClicked : undefined}
        onKeyDown={onRowContextMenu || onHeaderContextMenu ? handleGridKeyDown : undefined}
      />
      {fitsContent && <div ref={focusMarkerRef} aria-hidden className="pointer-events-none absolute left-0 w-px" />}
      {tooltip && (
        <div
          style={{ position: "absolute", left: tooltip.x, top: tooltip.y, transform: "translateX(-50%)" }}
          className="pointer-events-none z-50 rounded-md bg-slate-900 px-3 py-1.5 text-xs text-slate-50 shadow-sm"
        >
          {tooltip.text}
        </div>
      )}
    </div>
  );
};
